import type { ContextMenuAction } from '../../explorer/models/TreeModel';
import { launchers, type AiTaskId } from '../../ai-chat/launchers/launcherRegistry';
import { isAiLauncherDisabled, AI_CLI_REQUIRED_REASON } from '../../ai-chat/launchers/aiLauncherGate';

/**
 * Every "AI" submenu item id is namespaced under this prefix so it can never
 * collide with an object's other context-menu actions as T13 adds more
 * launcher entries to the same shared `actionId` space (T12 fix round 1, M-6).
 */
export const AI_TASK_ACTION_PREFIX = 'ai:';

/**
 * The submenu *trigger* id (the "AI" row itself, not one of its items). Never
 * dispatched anywhere — `TreeNodeContextMenu.tsx` opens it as a
 * `ContextMenuSubTrigger`, it never reaches `handleContextMenuAction` — but
 * namespaced under the same prefix for consistency with the items beneath it
 * (T12 fix round 2, NF-4).
 */
export const AI_MENU_TRIGGER_ID = `${AI_TASK_ACTION_PREFIX}menu`;

/**
 * Namespaced action id (e.g. `ai:explain-table`) -> the launcher id, or null
 * if `actionId` isn't a (known) AI-launcher action. Used by
 * `DatabaseExplorer.tsx`'s context-menu dispatcher to recognize + unwrap an
 * "AI" submenu click before falling through to the per-type executors.
 */
export function parseAiTaskActionId(actionId: string): AiTaskId | null {
  if (!actionId.startsWith(AI_TASK_ACTION_PREFIX)) return null;
  const id = actionId.slice(AI_TASK_ACTION_PREFIX.length);
  return id in launchers ? (id as AiTaskId) : null;
}

/**
 * Build the "AI" submenu shared by the table, view, and kusto-table context
 * menus (Object Explorer). One builder — the reviewer had to write a
 * separate mutant for each of the two hand-copied versions this replaces
 * (T12 fix round 1, M-2). Items are individually disabled (with a WHY
 * tooltip, repo convention) when no AI CLI is installed; `aiCliDetectionLoading`
 * keeps them enabled while detection is still in flight so they don't
 * flicker disabled → enabled once it resolves.
 *
 * Labels default to the launcher registry's own `label` (one copy of the
 * string, not a third); pass `labelOverrides` for a per-menu variant — e.g.
 * the view menu's "Explain this view" (T12 fix round 1, M-1).
 */
export function createAiSubmenu(
  taskIds: AiTaskId[],
  labelOverrides: Partial<Record<AiTaskId, string>> = {},
  aiCliAvailable?: boolean,
  aiCliDetectionLoading?: boolean,
): ContextMenuAction {
  const disabled = isAiLauncherDisabled(aiCliAvailable, aiCliDetectionLoading);
  const disabledReason = disabled ? AI_CLI_REQUIRED_REASON : undefined;
  return {
    id: AI_MENU_TRIGGER_ID,
    label: 'AI',
    icon: 'sparkles',
    submenu: taskIds.map((id) => ({
      id: `${AI_TASK_ACTION_PREFIX}${id}`,
      label: labelOverrides[id] ?? launchers[id].label,
      icon: 'sparkles',
      disabled,
      disabledReason,
    })),
  };
}
